var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useRef, useState } from 'react';
import deepEqual from 'fast-deep-equal';
import { DROPDOWN_WIDTH, useDropdown } from "../Dropdown";
import { createUIElement, KeyCode } from "../../helpers";
import { useControlState } from '../helpers/useControlState';
import { createDefaultOption, createEmptyMessageRenderer, createOptionContainerRenderer, Suggest } from "../../Suggest";
import { createDefaultControl } from './controls/defaultControlRenderer';
import { getStylesWithMaxWidth } from "../../helpers/getStylesWithMaxWidth";
import { CONTROL_SIZE } from "../../common/public";
function getSelectedIndex(options, value, valueGetter, comparator) {
    var result = undefined;
    options.some(function (item, idx) {
        if (comparator(valueGetter(item), value)) {
            result = idx;
            return true;
        }
        return false;
    });
    return result;
}
function defaultTextGetter(option) {
    if (typeof option === 'object' && typeof (option === null || option === void 0 ? void 0 : option['label']) === 'string') {
        return option['label'];
    }
    if (typeof option === 'object' && typeof (option === null || option === void 0 ? void 0 : option['text']) === 'string') {
        return option['text'];
    }
    console.error('Combobox error: You must define "textGetter" property or provide  "text"|"label" as string field in option data');
    return '';
}
export var Combobox = createUIElement('Combobox', function (_a, ref) {
    var className = _a.className, _b = _a.size, size = _b === void 0 ? CONTROL_SIZE.s : _b, _c = _a.disabled, disabled = _c === void 0 ? false : _c, _d = _a.error, error = _d === void 0 ? false : _d, texts = _a.texts, data = _a.data, value = _a.value, placeholder = _a.placeholder, onChange = _a.onChange, valueGetter = _a.valueGetter, _e = _a.valueComparator, valueComparator = _e === void 0 ? deepEqual : _e, _f = _a.optionContainerRenderer, optionContainerRenderer = _f === void 0 ? createOptionContainerRenderer() : _f, _g = _a.textGetter, textGetter = _g === void 0 ? defaultTextGetter : _g, onMenuOpen = _a.onMenuOpen, onMenuClose = _a.onMenuClose, isOptionHidden = _a.isOptionHidden, isOptionDisabled = _a.isOptionDisabled, virtualListWidth = _a.virtualListWidth, virtualListLimit = _a.virtualListLimit, iconGetter = _a.iconGetter, hasEnterKeyStopPropagation = _a.hasEnterKeyStopPropagation, apiRef = _a.apiRef, onFocus = _a.onFocus, onBlur = _a.onBlur, onClick = _a.onClick, onMouseDown = _a.onMouseDown, onMouseUp = _a.onMouseUp, onMouseEnter = _a.onMouseEnter, onMouseLeave = _a.onMouseLeave, onKeyDown = _a.onKeyDown, onKeyUp = _a.onKeyUp, onKeyPress = _a.onKeyPress, style = _a.style, rest = __rest(_a, ["className", "size", "disabled", "error", "texts", "data", "value", "placeholder", "onChange", "valueGetter", "valueComparator", "optionContainerRenderer", "textGetter", "onMenuOpen", "onMenuClose", "isOptionHidden", "isOptionDisabled", "virtualListWidth", "virtualListLimit", "iconGetter", "hasEnterKeyStopPropagation", "apiRef", "onFocus", "onBlur", "onClick", "onMouseDown", "onMouseUp", "onMouseEnter", "onMouseLeave", "onKeyDown", "onKeyUp", "onKeyPress", "style"]);
    var dataSelectorId = rest['data-selector-id'] || 'combobox';
    var controlRenderer = rest.controlRenderer ||
        createDefaultControl({
            iconGetter: iconGetter
        });
    var optionRenderer = rest.optionRenderer ||
        createDefaultOption({
            iconGetter: iconGetter
        });
    var rootRef = useRef(null);
    var buttonRef = ref || rootRef;
    var _h = useState(null), searchTerm = _h[0], setSearchTerm = _h[1];
    var _j = useControlState({
        rootRef: buttonRef,
        onBlur: function () {
            onBlur === null || onBlur === void 0 ? void 0 : onBlur();
            setSearchTerm(null);
        },
        onFocus: onFocus
    }), focused = _j.focused, active = _j.active, activate = _j.activate, preventBlur = _j.preventBlur, input = _j.input;
    var selectedIndex = getSelectedIndex(data, value, valueGetter, valueComparator);
    var selectedOption = (selectedIndex !== undefined &&
        (data[selectedIndex] || null));
    var control = useDropdown({
        ref: buttonRef,
        offset: 1,
        renderControl: function (_a) {
            var button = _a.button, dropdownActions = _a.actions, isActive = _a.isActive;
            var text = (selectedOption && textGetter(selectedOption)) || '';
            var actions = {
                open: function () {
                    if (disabled || isActive) {
                        return;
                    }
                    dropdownActions === null || dropdownActions === void 0 ? void 0 : dropdownActions.show();
                    activate();
                },
                close: function () {
                    if (disabled || !isActive) {
                        return;
                    }
                    dropdownActions === null || dropdownActions === void 0 ? void 0 : dropdownActions.hide();
                    activate();
                }
            };
            var onSearchTermChange = function (term) {
                setSearchTerm(term);
                if (!isActive && term !== null) {
                    actions.open();
                }
            };
            if (apiRef) {
                apiRef.current = actions;
            }
            return controlRenderer(__assign(__assign({}, rest), { className: "vm-Combobox " + className, testId: dataSelectorId, error: !!error, size: size, disabled: !!disabled, active: active, opened: isActive, actions: actions,
                focused: focused,
                text: text,
                value: value,
                hasEnterKeyStopPropagation: hasEnterKeyStopPropagation,
                placeholder: placeholder, searchTerm: typeof searchTerm === 'undefined' ? null : searchTerm, option: selectedOption, onSearchTermChange: onSearchTermChange, onClear: function () {
                    setSearchTerm(null);
                    onChange === null || onChange === void 0 ? void 0 : onChange(undefined, -1);
                }, control: {
                    ref: button.ref,
                    onBlur: input.onBlur,
                    onFocus: input.onFocus,
                    onKeyDown: function (e) {
                        if (e.keyCode === KeyCode.Space && !disabled) {
                            isActive ? actions.close() : actions.open();
                        }
                        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
                    },
                    onMouseDown: function (e) {
                        e === null || e === void 0 ? void 0 : e.stopPropagation();
                        actions.open();
                        onMouseDown === null || onMouseDown === void 0 ? void 0 : onMouseDown(e);
                    },
                    onMouseUp: onMouseUp,
                    onClick: onClick,
                    onMouseEnter: onMouseEnter,
                    onMouseLeave: onMouseLeave,
                    onKeyUp: onKeyUp,
                    onKeyPress: onKeyPress,
                    style: style
                } }));
        },
        renderPopover: function (_a) {
            var actions = _a.actions, popover = _a.popover;
            var suggestStyle = getStylesWithMaxWidth(popover.style);
            var suggestVirtualListWidth = virtualListWidth ||
                (typeof (suggestStyle === null || suggestStyle === void 0 ? void 0 : suggestStyle.minWidth) === 'number' ? suggestStyle === null || suggestStyle === void 0 ? void 0 : suggestStyle.minWidth : undefined);
            return (React.createElement(Suggest, { "data-selector-id": dataSelectorId + "-menu", filter: function (item) { return !(isOptionHidden === null || isOptionHidden === void 0 ? void 0 : isOptionHidden(item)); }, searchTerm: searchTerm || '', ref: popover.ref, selectedIndex: selectedIndex, renderEmptyMessage: createEmptyMessageRenderer({
                    text: (texts === null || texts === void 0 ? void 0 : texts.empty) || 'No options found'
                }), textGetter: textGetter, onSelect: function (item, idx) {
                    onChange === null || onChange === void 0 ? void 0 : onChange(valueGetter(item), idx);
                    setSearchTerm(null);
                    actions.hide();
                }, onMouseDown: preventBlur, optionRenderer: optionRenderer, optionContainerRenderer: optionContainerRenderer, isOptionDisabled: isOptionDisabled, data: data, style: suggestStyle, hasVirtualization: true, virtualListWidth: suggestVirtualListWidth, virtualListLimit: virtualListLimit }));
        },
        onDidHide: onMenuClose,
        onDidShow: onMenuOpen,
        dropdownWidth: size === CONTROL_SIZE.full
            ? DROPDOWN_WIDTH.controlWidth
            : DROPDOWN_WIDTH.minControlWidthAndMaxXXL
    })[0];
    return control;
});
//# sourceMappingURL=Combobox.js.map